import { PROJECT_SHIVA_TAG, BILLING_UPSELL_DISCOUNT } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { combineStatus } from 'commons/utils/combineStatus';
import { useGetIsFreePlan } from 'domains/Auth/hooks';
import useGetPlanDebt from 'domains/Auth/hooks/useGetPlanDebt/useGetPlanDebt';
import useGetStorePaymentDiscount from 'domains/Auth/hooks/useGetStorePaymentDiscount';
import { usePlanDowngrade } from 'domains/Billing/BillingPlans/hooks';
import { useGetStoreDiscountsQuery } from 'domains/Billing/Checkout/CheckoutApi/checkoutApi';
import {
  useGetLastPayment,
  useGetRecurrentPaymentConcepts,
} from 'domains/Billing/Checkout/hooks';
import { BillingUpsellDiscount } from './BillingUpsellDiscount';
import DowngradeAdBar from './DowngradeAdBar';
import { ExpiredStoreAdBar } from './ExpiredStoreAdBar';
import { PaymentFailedAdBar } from './PaymentFailedAdBar';
import { PaymentInProcessAdBar } from './PaymentInProcessAdBar';
import { PlanDebtAdBar } from './PlanDebtAdBar';
import { PostUpsellPaymentInvitation } from './PostUpsellPaymentInvitation';
import { StorePaymentDiscount } from './StorePaymentDiscount';
import { useTopAdBar } from './useTopAdBar';
import { useExpirationBanner } from '../ExpirationBanner/useExpirationBanner';

export function TopAdBar() {
  const { showExpirationBanner, isExpired } = useExpirationBanner();
  const { isFreePlan, isTrial } = useGetIsFreePlan();
  const shouldShowExpirationBanner =
    !isTrial && isExpired && showExpirationBanner;
  const storePaymentDiscount = useGetStorePaymentDiscount();
  const { planDebtData, shouldUseDebtData } = useGetPlanDebt();
  const [isShivaProject, isBillingUpsellDiscount] = useHasTags([
    PROJECT_SHIVA_TAG,
    BILLING_UPSELL_DISCOUNT,
  ]);
  const { isShowTopAdBar } = useTopAdBar();
  const { scheduledDowngrade } = usePlanDowngrade(true, false);
  const {
    recurrentPaymentForPlanActivated,
    status: getRecurrentPaymentConceptsStatus,
  } = useGetRecurrentPaymentConcepts(shouldShowExpirationBanner);
  const { data: storeDiscounts } = useGetStoreDiscountsQuery(undefined, {
    skip: !isBillingUpsellDiscount,
  });
  const { paymentStatus, status: getPaymentStatus } =
    useGetLastPayment('plan-cost');

  const { isSuccess } = combineStatus(
    getPaymentStatus,
    getRecurrentPaymentConceptsStatus,
  );

  if (!isShowTopAdBar) return null;

  if (shouldUseDebtData && isSuccess && paymentStatus === 'IN_PROCESS') {
    return <PaymentInProcessAdBar />;
  }
  if (scheduledDowngrade?.scheduledAt) {
    return <DowngradeAdBar />;
  }

  if (isBillingUpsellDiscount) {
    const upsellDiscount = storeDiscounts?.find(
      (discount) => discount.description === BILLING_UPSELL_DISCOUNT,
    );
    if (upsellDiscount && upsellDiscount.expires_at) {
      const expirationDate = new Date(upsellDiscount.expires_at);
      if (expirationDate.getTime() > Date.now()) {
        return <BillingUpsellDiscount expirationDate={expirationDate} />;
      }
    }
  }

  if (
    shouldShowExpirationBanner &&
    isSuccess &&
    !recurrentPaymentForPlanActivated &&
    !shouldUseDebtData
  ) {
    return <ExpiredStoreAdBar />;
  }

  if (shouldUseDebtData && isSuccess && paymentStatus === 'FAILED') {
    return <PaymentFailedAdBar />;
  }

  if (storePaymentDiscount) {
    return (
      <StorePaymentDiscount
        percentage={storePaymentDiscount.percentage}
        expirationDate={new Date(storePaymentDiscount.expirationDate)}
      />
    );
  }

  if (isSuccess && paymentStatus === 'IN_PROCESS') {
    return null;
  }

  if (isTrial && !isFreePlan && !isShivaProject) {
    return <PostUpsellPaymentInvitation />;
  }

  if (
    isSuccess &&
    !recurrentPaymentForPlanActivated &&
    planDebtData?.paidUntilDate &&
    planDebtData?.adminDownDate &&
    shouldUseDebtData
  ) {
    return <PlanDebtAdBar />;
  }

  return null;
}
